import { useCallback, useRef } from 'react';

/**
 * A hook that takes a function and a cooldown duration and returns a new function that will only call the original if
 * the cooldown period has been expired.
 *
 * @param {Function} fn A function to call after the cooldown period has expired.
 * @param {number} cooldown The cooldown time in milliseconds. Default is 15 seconds.
 * @returns {Function} The original function wrapped in a cooldown.
 */
const useCooldown = (fn, cooldown = 15 * 1000) => {
  const lastCalledRef = useRef(0);

  const cooldownFunction = useCallback(
    async (...args) => {
      const now = Date.now();

      if (now - lastCalledRef.current >= cooldown) {
        lastCalledRef.current = now;
        await fn(...args);
      }
    },
    [fn, cooldown],
  );
  return cooldownFunction;
};

export { useCooldown };
